import { useEffect, useMemo, useState } from "react"
import { Link } from "@inertiajs/react"
import { ArrowRight, CalendarDays, Clock3, ExternalLink, Radio, RefreshCw } from "lucide-react"
import type { PublicLocale } from "@/lib/public-links"

type Departure = {
  time: string
  day_offset: number
  destination_name?: string | null
}

type Departures = Record<string, Departure[]>

type SchedulePeriod = {
  valid_from: string
  valid_until: string
  days_of_week: number[]
  included_dates: string[]
  excluded_dates: string[]
  priority: number
  departures: Departures
}

type Webcam = {
  port_slug: string
  name: string
  image_urls: string[]
  source_url: string
}

export type FerryPortData = {
  year: number
  valid_from: string
  valid_until: string
  next_departure_scope: "port" | "route"
  time_zone: string
  today: string
  current_port_slug: string
  current_port_name: string
  destination_name: string
  route: {
    name: string
    path: string
    operator_name: string | null
    official_url: string
  }
  ports: Record<string, string>
  periods: SchedulePeriod[]
  next_departure: {
    time: string
    departs_at: string
    port_slug: string
    port_name: string
    destination_name: string
  } | null
  webcams: Webcam[]
}

const COPY = {
  en: {
    liveCameras: "Live port cameras",
    cameraNote: "Current traffic views · refreshes every minute",
    refresh: "Refresh images",
    cameraSource: "Camera source: HAK",
    cameraAlt: "live traffic camera, view",
    nextFrom: "Next departure from",
    to: "to",
    departsIn: "Departs in",
    minute: "min",
    hours: "hr",
    noMoreToday: "No later departure is listed in the 2026 schedule.",
    timetable: "2026 departures",
    chooseDate: "Choose a travel date",
    from: "From",
    official: "Open official timetable",
    routeDetails: "Route details",
    timeZone: "Times shown in local Croatian time.",
    checkAgain: "Schedules can change. Recheck the operator notice on your travel day.",
    specialSchedule: "Special schedule applies on the selected date",
  },
  de: {
    liveCameras: "Live-Kameras der Fährhäfen",
    cameraNote: "Aktuelle Verkehrsbilder · jede Minute aktualisiert",
    refresh: "Bilder aktualisieren",
    cameraSource: "Kameraquelle: HAK",
    cameraAlt: "Live-Verkehrskamera, Ansicht",
    nextFrom: "Nächste Abfahrt ab",
    to: "nach",
    departsIn: "Abfahrt in",
    minute: "Min.",
    hours: "Std.",
    noMoreToday: "Im Fahrplan 2026 ist keine spätere Abfahrt eingetragen.",
    timetable: "Abfahrten 2026",
    chooseDate: "Reisedatum wählen",
    from: "Ab",
    official: "Offiziellen Fahrplan öffnen",
    routeDetails: "Streckendetails",
    timeZone: "Alle Zeiten in kroatischer Ortszeit.",
    checkAgain: "Fahrpläne können sich ändern. Am Reisetag nochmals beim Betreiber prüfen.",
    specialSchedule: "Am gewählten Datum gilt ein Sonderfahrplan",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export function FerryPortWebcams({ locale, ferryPort }: { locale: PublicLocale; ferryPort: FerryPortData }) {
  const copy = COPY[locale]
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    const interval = window.setInterval(() => setRefreshKey(Date.now()), 60_000)
    return () => window.clearInterval(interval)
  }, [])

  if (ferryPort.webcams.length === 0) return null

  return (
    <section className="mt-3 overflow-hidden rounded-2xl bg-ink-display" aria-labelledby="ferry-cameras-title">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 text-surface md:px-5">
        <Radio className="h-4 w-4 text-accent-faded" aria-hidden />
        <div>
          <h2 id="ferry-cameras-title" className="font-sans text-sm font-semibold text-surface">{copy.liveCameras}</h2>
          <p className="text-xs text-surface-subtle">{copy.cameraNote}</p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey(Date.now())}
          className="ml-auto inline-flex h-9 items-center gap-2 rounded-full border border-surface/25 px-3 text-xs font-semibold text-surface transition-colors hover:bg-surface/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          {copy.refresh}
        </button>
      </div>

      <div className={`grid gap-3 bg-surface-subtle p-3 ${ferryPort.webcams.length > 1 ? "lg:grid-cols-2" : ""}`}>
        {ferryPort.webcams.map((camera) => (
          <figure key={camera.port_slug} className="self-start overflow-hidden rounded-xl bg-surface">
            <figcaption className="flex items-center gap-2 border-b border-hairline px-3 py-2.5 text-sm font-semibold text-ink-display">
              <span className="h-2 w-2 rounded-full bg-verified" aria-hidden />
              {camera.name}
            </figcaption>
            <div className="grid grid-cols-2 gap-px bg-hairline">
              {camera.image_urls.map((url, index) => (
                <img
                  key={url}
                  src={refreshKey ? `${url}?v=${refreshKey}` : url}
                  alt={`${camera.name} ${copy.cameraAlt} ${index + 1}`}
                  loading={index === 0 ? "eager" : "lazy"}
                  referrerPolicy="no-referrer"
                  className="aspect-[4/3] h-full w-full bg-surface-sunken object-cover"
                />
              ))}
            </div>
          </figure>
        ))}
      </div>

      <a
        href={ferryPort.webcams[0].source_url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 px-4 py-2.5 text-xs text-surface-subtle hover:text-surface"
      >
        {copy.cameraSource}
        <ExternalLink className="h-3 w-3" aria-hidden />
      </a>
    </section>
  )
}

export function FerryPortSchedule({
  locale,
  ferryPort,
  showRouteDetails = true,
}: {
  locale: PublicLocale
  ferryPort: FerryPortData
  showRouteDetails?: boolean
}) {
  const copy = COPY[locale]
  const [selectedDate, setSelectedDate] = useState(ferryPort.today)
  const [clock, setClock] = useState<Date | null>(null)

  useEffect(() => {
    setClock(new Date())
    const interval = window.setInterval(() => setClock(new Date()), 30_000)
    return () => window.clearInterval(interval)
  }, [])

  const departures = useMemo(() => scheduleForDate(ferryPort, selectedDate), [ferryPort, selectedDate])
  const liveNext = clock ? nextDeparture(ferryPort, clock) : ferryPort.next_departure
  const liveDate = clock ? zonedDateParts(clock, ferryPort.time_zone).date : ferryPort.today
  const minutesUntil = clock && liveNext
    ? Math.max(1, Math.ceil((new Date(liveNext.departs_at).getTime() - clock.getTime()) / 60_000))
    : null
  const portSlugs = Object.keys(ferryPort.ports)
  const rowCount = Math.max(...portSlugs.map((slug) => departures[slug]?.length ?? 0), 0)
  const selectedIsLive = selectedDate === liveDate
  const specialSchedule = (schedulePeriodForDate(ferryPort, selectedDate)?.priority ?? 0) > 0

  return (
    <section className="public-panel mt-6 overflow-hidden" aria-labelledby="ferry-timetable-title">
      <div className="bg-ink-display px-4 py-5 text-surface md:px-6 md:py-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-surface-subtle">
          <Clock3 className="h-4 w-4" aria-hidden />
          {copy.nextFrom} {liveNext?.port_name ?? ferryPort.current_port_name}
        </div>
        {liveNext ? (
          <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
            <strong className="font-display text-5xl font-semibold leading-none text-surface md:text-6xl">{liveNext.time}</strong>
            <div className="pb-1">
              <p className="flex items-center gap-2 font-semibold text-surface">
                {copy.to} {liveNext.destination_name}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </p>
              {minutesUntil && <p className="mt-0.5 text-sm text-surface-subtle">{copy.departsIn} {durationLabel(minutesUntil, copy)}</p>}
            </div>
          </div>
        ) : (
          <p className="mt-3 text-surface-subtle">{copy.noMoreToday}</p>
        )}
      </div>

      <div className="flex flex-col gap-4 border-b border-hairline px-4 py-4 md:flex-row md:items-end md:justify-between md:px-6">
        <div>
          <h2 id="ferry-timetable-title" className="public-section-title">{copy.timetable}</h2>
          <p className="mt-1 text-sm text-ink-muted">{ferryPort.route.name} · {ferryPort.route.operator_name}</p>
        </div>
        <label className="w-full md:w-auto">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-secondary">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden />
            {copy.chooseDate}
          </span>
          <input
            type="date"
            min={ferryPort.valid_from}
            max={ferryPort.valid_until}
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="form-control bg-surface md:w-[11rem]"
          />
        </label>
      </div>

      {specialSchedule && <p className="border-b border-hairline bg-signal-faded px-4 py-2 text-xs font-semibold text-signal md:px-6">{copy.specialSchedule}</p>}

      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-left">
          <thead>
            <tr>
              {portSlugs.map((slug) => (
                <th key={slug} scope="col" className="w-1/2 border-b border-hairline bg-surface-subtle px-4 py-3 md:px-6">
                  <span className="block text-xs font-semibold text-ink-muted">{copy.from}</span>
                  <span className="mt-0.5 block font-display text-lg font-semibold text-ink-display">{ferryPort.ports[slug]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }, (_, rowIndex) => (
              <tr key={rowIndex} className="border-b border-hairline last:border-b-0">
                {portSlugs.map((slug) => {
                  const departure = departures[slug]?.[rowIndex]
                  const isNext = selectedIsLive && slug === liveNext?.port_slug && departure?.time === liveNext.time
                  return (
                    <td key={slug} className={`px-4 py-2.5 font-semibold tabular-nums md:px-6 ${isNext ? "bg-accent-faded text-accent" : "text-ink-body"}`}>
                      {departure?.time ?? "—"}
                      {departure?.destination_name && (
                        <span className="ml-2 text-xs font-normal text-ink-muted">→ {departure.destination_name}</span>
                      )}
                      {isNext && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-accent" aria-label="Next departure" />}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-hairline bg-surface-subtle px-4 py-4 text-sm md:flex-row md:items-center md:px-6">
        <div className="max-w-2xl">
          <p className="font-semibold text-ink-secondary">{copy.timeZone}</p>
          <p className="text-ink-muted">{copy.checkAgain}</p>
        </div>
        <div className="flex flex-wrap gap-2 md:ml-auto">
          {showRouteDetails && <Link href={ferryPort.route.path} className="btn btn-secondary">{copy.routeDetails}</Link>}
          <a href={ferryPort.route.official_url} target="_blank" rel="noreferrer" className="btn btn-primary">
            {copy.official}
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </section>
  )
}

function scheduleForDate(ferryPort: FerryPortData, date: string) {
  return schedulePeriodForDate(ferryPort, date)?.departures ?? {}
}

function schedulePeriodForDate(ferryPort: FerryPortData, date: string) {
  return ferryPort.periods.reduce<SchedulePeriod | undefined>((selected, period) => {
    if (!periodAppliesOn(period, date)) return selected
    if (!selected || period.priority > selected.priority) return period
    return selected
  }, undefined)
}

function nextDeparture(ferryPort: FerryPortData, now: Date) {
  const local = zonedDateParts(now, ferryPort.time_zone)
  const departurePortSlugs = ferryPort.next_departure_scope === "route"
    ? Object.keys(ferryPort.ports)
    : [ferryPort.current_port_slug]
  const candidates = [-1, 0, 1, 2].flatMap((dayOffset) => {
    const scheduleDate = addDays(local.date, dayOffset)
    const schedule = scheduleForDate(ferryPort, scheduleDate)
    return departurePortSlugs.flatMap((portSlug) => {
      const destinationSlug = Object.keys(ferryPort.ports).find((slug) => slug !== portSlug)
      return (schedule[portSlug] ?? []).map((departure) => {
        const [hour, minute] = departure.time.split(":").map(Number)
        const serviceDayOffset = dayOffset + departure.day_offset
        const minutesFromNow = (serviceDayOffset * 1_440) + (hour * 60) + minute - local.minutes
        return {
          time: departure.time,
          minutesFromNow,
          port_slug: portSlug,
          port_name: ferryPort.ports[portSlug],
          destination_name: departure.destination_name
            ?? (destinationSlug ? ferryPort.ports[destinationSlug] : ferryPort.destination_name),
        }
      })
    })
  }).filter((candidate) => candidate.minutesFromNow > 0)

  const next = candidates.sort((left, right) => left.minutesFromNow - right.minutesFromNow)[0]
  if (!next) return null

  return {
    time: next.time,
    departs_at: new Date(now.getTime() + (next.minutesFromNow * 60_000)).toISOString(),
    port_slug: next.port_slug,
    port_name: next.port_name,
    destination_name: next.destination_name,
  }
}

function zonedDateParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "00"
  return {
    date: `${value("year")}-${value("month")}-${value("day")}`,
    minutes: (Number(value("hour")) * 60) + Number(value("minute")),
  }
}

function durationLabel(minutes: number, copy: (typeof COPY)[PublicLocale]) {
  if (minutes < 60) return `${minutes} ${copy.minute}`
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder > 0 ? `${hours} ${copy.hours} ${remainder} ${copy.minute}` : `${hours} ${copy.hours}`
}

function periodAppliesOn(period: SchedulePeriod, date: string) {
  if (date < period.valid_from || date > period.valid_until || period.excluded_dates.includes(date)) return false
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay()
  return period.included_dates.includes(date) || period.days_of_week.includes(weekday)
}

function addDays(date: string, days: number) {
  const parsed = new Date(`${date}T12:00:00Z`)
  parsed.setUTCDate(parsed.getUTCDate() + days)
  return parsed.toISOString().slice(0, 10)
}
